import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Table, Space, Tag, Typography, Modal, message, Breadcrumb, Segmented, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import {
    INIT_SALE_DATA, MOCK_SCOPE_ENTERPRISES, CURRENT_ENTERPRISE_ID,
} from './mockData';
import EntryFormDialog from './components/EntryFormDialog';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  自定义售卖 - 功能入口列表
// ══════════════════════════════════════════════

const getDefaultView = () => {
    try {
        const info = JSON.parse(localStorage.getItem('user_info') || '{}');
        return info.role === 'platform' ? 'platform' : 'enterprise';
    } catch {
        return 'enterprise';
    }
};

const CustomSaleEntry = () => {
    const navigate = useNavigate();
    const [view, setView] = useState(getDefaultView); // platform | enterprise
    const [scopeEnt, setScopeEnt] = useState(CURRENT_ENTERPRISE_ID);
    const [data, setData] = useState(() => JSON.parse(JSON.stringify(INIT_SALE_DATA)));
    const [dialog, setDialog] = useState({ open: false, editing: null });

    const currentEntId = view === 'platform' ? scopeEnt : CURRENT_ENTERPRISE_ID;
    const currentEntName = MOCK_SCOPE_ENTERPRISES.find((e) => e.id === currentEntId)?.name || '-';
    const entries = useMemo(() => {
        const list = data[currentEntId]?.entries || [];
        return [...list].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    }, [data, currentEntId]);

    const handleSave = (values) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const ent = next[currentEntId] || { entries: [] };
            if (dialog.editing) {
                ent.entries = ent.entries.map((e) => (e.id === dialog.editing.id ? { ...e, ...values } : e));
            } else {
                ent.entries = [...ent.entries, {
                    id: `SE_${Date.now()}`, ...values, categories: [], packages: [],
                }];
            }
            next[currentEntId] = ent;
            return next;
        });
        message.success(dialog.editing ? '编辑成功' : '新增成功');
        setDialog({ open: false, editing: null });
    };

    const handleDelete = (record) => {
        if ((record.packages || []).length > 0) {
            message.warning('该功能入口下已有套餐，不能删除');
            return;
        }
        Modal.confirm({
            title: '删除功能入口',
            content: `确定删除功能入口「${record.name}」？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => {
                setData((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    next[currentEntId].entries = next[currentEntId].entries.filter((e) => e.id !== record.id);
                    return next;
                });
                message.success('删除成功');
            },
        });
    };

    const columns = [
        ...(view === 'platform' ? [{
            title: '所属企业', key: 'enterprise', width: 240,
            render: () => (
                <div>
                    <div>{currentEntName}</div>
                    <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{currentEntId}</span>
                </div>
            ),
        }] : []),
        {
            title: '功能入口', key: 'name', width: 220,
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{r.code}</span>
                </div>
            ),
        },
        { title: '排序', dataIndex: 'sort', key: 'sort', width: 90, align: 'center' },
        {
            title: '套餐配置', key: 'packageCount', width: 150, align: 'center',
            render: (_, r) => (
                <Button type="link" size="small" style={{ padding: 0 }} icon={<EyeOutlined />}
                    onClick={() => navigate(`/operation/custom-sale/config?id=${r.id}&ent=${currentEntId}`)}>
                    {(r.packages || []).length} 个套餐
                </Button>
            ),
        },
        {
            title: '操作', key: 'action', width: 220, align: 'center', fixed: 'right',
            render: (_, r) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<SettingOutlined />}
                        onClick={() => navigate(`/operation/custom-sale/config?id=${r.id}&ent=${currentEntId}`)}>配置</Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setDialog({ open: true, editing: r })}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r)}>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, background: '#f5f6f8', minHeight: '100vh' }}>
            <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: '运营管理' }, { title: '套餐售卖' }, { title: '自定义售卖' }]} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>自定义售卖</Title>
                <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
                    <Segmented
                        value={view}
                        onChange={setView}
                        options={[{ label: '平台视角', value: 'platform' }, { label: '企业视角', value: 'enterprise' }]}
                    />
                </Space>
            </div>

            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>功能入口</span>
                        {view === 'platform' && (
                            <Select
                                size="small" style={{ width: 260 }} value={scopeEnt} onChange={setScopeEnt}
                                options={MOCK_SCOPE_ENTERPRISES.map((e) => ({ label: e.name, value: e.id }))}
                            />
                        )}
                        <Tag color="blue">{view === 'platform' ? '平台全量查看' : '本企业'}</Tag>
                    </Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setDialog({ open: true, editing: null })}>新增功能入口</Button>
                </div>

                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
                    功能入口 = 企业在 App 上的售卖位置，由企业自定义；点击「套餐配置」进入该入口的套餐售卖清单。
                </Text>

                <Table
                    dataSource={entries}
                    columns={columns}
                    rowKey="id"
                    bordered={false}
                    scroll={{ x: 900 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                    locale={{ emptyText: <Text type="secondary">暂无功能入口，点击右上角「新增功能入口」</Text> }}
                />
            </Card>

            <EntryFormDialog
                open={dialog.open}
                editData={dialog.editing}
                existingCodes={entries}
                onCancel={() => setDialog({ open: false, editing: null })}
                onSubmit={handleSave}
            />
        </div>
    );
};

export default CustomSaleEntry;
